import React, { useState } from "react";
import { cn } from "@shadcn-in-nx/utils";
import {
    ChevronDown, ChevronUp, CheckCircle, AlertCircle, Clock,
    Code, FileText, Search, Edit3, Trash2, PlusCircle, FolderSearch,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import ToolArgumentsView from "./ToolArgumentsView";
import ToolResponseView from "./ToolResponseView";

const toolIcons = {
    read_file: FileText,
    read_files: FileText,
    execute_code: Code,
    create_file: PlusCircle,
    update_file: Edit3,
    edit_file: Edit3,
    delete_file: Trash2,
    search_in_files: Search,
    grep_search: Search,
    glob_search: FolderSearch,
    explore_codebase: FolderSearch,
};

const toolColors = {
    read_file: "blue",
    read_files: "blue",
    execute_code: "purple",
    create_file: "green",
    update_file: "amber",
    edit_file: "amber",
    delete_file: "red",
    search_in_files: "cyan",
    grep_search: "cyan",
    glob_search: "indigo",
    explore_codebase: "violet",
};

export default function ToolCallDetail({ toolCall, toolResponse }) {
    const [showArgs, setShowArgs] = useState(true);
    const [showResponse, setShowResponse] = useState(true);

    const toolName = toolCall.toolName || "unknown";
    const hasError = toolResponse?.functionResponse?.success === false ||
        toolResponse?.functionResponse?.error;

    const Icon = toolIcons[toolName] || Code;
    const color = toolColors[toolName] || "gray";

    return (
        <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
            {/* Header */}
            <div className={cn(
                "px-3 py-2 border-b",
                hasError ? "bg-red-50" : `bg-${color}-50`
            )}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className={cn(
                            "p-1.5 rounded",
                            hasError ? "bg-red-100" : `bg-${color}-100`
                        )}>
                            <Icon className={cn(
                                "w-4 h-4",
                                hasError ? "text-red-600" : `text-${color}-600`
                            )} />
                        </div>
                        <div>
                            <span className="font-medium text-sm text-gray-800">{toolName}</span>
                            <div className="flex items-center gap-2">
                                {hasError ? (
                                    <span className="flex items-center gap-1 text-[10px] text-red-600">
                                        <AlertCircle className="w-2.5 h-2.5" /> Error
                                    </span>
                                ) : toolResponse ? (
                                    <span className="flex items-center gap-1 text-[10px] text-green-600">
                                        <CheckCircle className="w-2.5 h-2.5" /> Done
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-1 text-[10px] text-gray-500">
                                        <Clock className="w-2.5 h-2.5" /> Pending
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <span className="text-[10px] text-gray-500">
                        {formatDistanceToNow(new Date(toolCall.createdAt), { addSuffix: true })}
                    </span>
                </div>
            </div>

            {/* Arguments Section */}
            <div className="border-b">
                <button
                    onClick={() => setShowArgs(!showArgs)}
                    className="w-full px-3 py-2 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                    <span className="text-xs font-medium text-gray-700">Arguments</span>
                    {showArgs ? <ChevronUp className="w-3 h-3 text-gray-500" /> : <ChevronDown className="w-3 h-3 text-gray-500" />}
                </button>
                {showArgs && (
                    <div className="p-3 bg-white">
                        <ToolArgumentsView toolName={toolName} args={toolCall.functionCall || {}} />
                    </div>
                )}
            </div>

            {/* Response Section */}
            {toolResponse && (
                <div>
                    <button
                        onClick={() => setShowResponse(!showResponse)}
                        className={cn(
                            "w-full px-3 py-2 flex items-center justify-between transition-colors",
                            hasError ? "bg-red-50 hover:bg-red-100" : "bg-green-50 hover:bg-green-100"
                        )}
                    >
                        <span className="text-xs font-medium text-gray-700">Response</span>
                        {showResponse ? <ChevronUp className="w-3 h-3 text-gray-500" /> : <ChevronDown className="w-3 h-3 text-gray-500" />}
                    </button>
                    {showResponse && (
                        <div className="p-3 bg-white">
                            <ToolResponseView
                                toolName={toolName}
                                response={toolResponse.functionResponse || {}}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
